import { ApiClient } from "../config/axiosConfig";
import { Status } from "../enum/common";
import {
  AttendanceSummaryDto,
  IAttendanceQuery,
  IAttendanceRecord,
  IAttendanceTimesheetRaw,
  ICreateEmployee,
  ICreateLeaveRequest,
  ICreateMeetingRoom,
  ICreateMeetingSchedule,
  ICreateStationeryPeriod,
  ICreateStationeryProduct,
  ICreateStationeryRequest,
  ICreateStationeryRequestItem,
  IEmployeeByDepartment,
  IEmployeeQuery,
  IEmployeer,
  IEmployeeUpdate,
  IEmployerDetail,
  ILeaveRequest,
  IMeetingRoom,
  IMeetingSchedule,
  IOvertime,
  IOvertimeRequestQuery,
  IPullAttendanceDto,
  IQuery,
  IQueryStationeryPeriod,
  IQueryStationeryProduct,
  IReviewStationeryRequestItem,
  ISolveAttendanceQuery,
  IStationeryCategory,
  IStationeryPeriod,
  IStationeryPeriodDetail,
  IStationeryProduct,
  IStationeryRequest,
  IStationeryRequestItem,
  IUpdateMeetingRoom,
  IUpdateMeetingSchedule,
  IUpdateStationeryProduct,
  IUpdateStationeryRequestItem,
} from "../models/Hrm";
import { IPaginatedResponse } from "../models/Pagination";
import { IUserInfo } from "../models/Users";

const buildQueryParams = (query?: any) => {
  if (!query) return undefined;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  return params;
};

export const hrmService = {
  getAllEmployee: (query?: IEmployeeQuery) => {
    const res = ApiClient.GET<IPaginatedResponse<IEmployeer>>(
      "/hrm/employee",
      query,
    );
    return res;
  },

  getUsersWithoutEmployee: (query?: IQuery) => {
    const res = ApiClient.GET<IPaginatedResponse<IUserInfo>>(
      "/hrm/employee/not-has-user",
      query,
    );
    return res;
  },

  getEmployeeById: (id: string, query?: IEmployeeQuery) => {
    const res = ApiClient.GET<IEmployerDetail>(`/hrm/employee/${id}`, query);
    return res;
  },
  getEmployeeByDepartment: (departmentCode: string) => {
    const res = ApiClient.GET<IEmployeeByDepartment[]>(
      `/hrm/employee/${departmentCode}/department`,
    );
    return res;
  },
  getMyEmployeeInfo: () => {
    const res = ApiClient.GET<IEmployerDetail>("/hrm/employee/me");
    return res;
  },

  createEmployee: (data: Partial<ICreateEmployee>) => {
    const res = ApiClient.POST<IEmployeer>("/hrm/employee", data);
    return res;
  },

  updateEmployee: (employeeId: string, data: IEmployeeUpdate) => {
    const res = ApiClient.PATCH<IEmployeer>(
      `/hrm/employee/${employeeId}`,
      data,
    );
    return res;
  },
  getAllAttendance: (query?: IAttendanceQuery) => {
    const res = ApiClient.GET<IPaginatedResponse<IAttendanceRecord>>(
      "/hrm/attendances",
      buildQueryParams(query),
    );
    return res;
  },
  getAttendanceByEmployee: (employeeId: string, query?: IAttendanceQuery) => {
    const res = ApiClient.GET<IPaginatedResponse<IAttendanceRecord>>(
      `/hrm/attendances/employee/${employeeId}`,
      buildQueryParams(query),
    );
    return res;
  },
  getMyAttendance: (query?: IAttendanceQuery) =>
    ApiClient.GET<IPaginatedResponse<IAttendanceRecord>>(
      "/hrm/attendances/me",
      buildQueryParams(query),
    ),
  getHikivisionData: (data: IPullAttendanceDto) =>
    ApiClient.POST<IPaginatedResponse<IAttendanceRecord>>(
      `/hrm/attendances/pull-data-hikivision`,
      data,
    ),

  solveAttendance: (data: ISolveAttendanceQuery, employeeId: String) =>
    ApiClient.POST<AttendanceSummaryDto>(
      `/hrm/attendances/solve/employee/${employeeId}`,
      data,
    ),

  getTimeSheetAttendance: (data?: ISolveAttendanceQuery, employeeId?: String) =>
    ApiClient.GET<IAttendanceTimesheetRaw>(
      `/hrm/attendances/timesheet/employee/${employeeId}`,
      data,
    ),

  createLeaveRequest: (data: ICreateLeaveRequest) => {
    return ApiClient.POST<ILeaveRequest>("/hrm/leave-requests", data);
  },
  cancelLeaveRequest: (id: string) =>
    ApiClient.PATCH<ILeaveRequest>(`/hrm/leave-requests/me/${id}`),

  reviewLeaveRequest: (id: string, isApprove: boolean) => {
    return ApiClient.PATCH<ILeaveRequest>(
      `/hrm/leave-requests/${id}/${isApprove ? "approve" : "reject"}`,
    );
  },
  getMyLeaveRequest: (query?: any) => {
    return ApiClient.GET<IPaginatedResponse<ILeaveRequest>>(
      "/hrm/leave-requests/me",
      query,
    );
  },

  getAllLeaveRequests: (query?: any) =>
    ApiClient.GET<IPaginatedResponse<ILeaveRequest>>(
      "/hrm/leave-requests",
      query,
    ),
  getLeaveRequestsByManager: (query?: any) =>
    ApiClient.GET<IPaginatedResponse<ILeaveRequest>>(
      "/hrm/leave-requests/my-department",
      query,
    ),
  getLeaveRequestByEmployee: (employeeId: string, query?: any) =>
    ApiClient.GET<IPaginatedResponse<ILeaveRequest>>(
      `/hrm/leave-requests/employee/${employeeId}`,
      query,
    ),
  getOneLeaveRequest: (id: string) =>
    ApiClient.GET<ILeaveRequest>(`/hrm/leave-requests/${id}`),

  createOvertimeRequest: (data: any) =>
    ApiClient.POST<IOvertime>("/hrm/overtime-requests", data),
  updateOvertimeRequest: (id: string, data: any) =>
    ApiClient.PATCH<IOvertime>(`/hrm/overtime-requests/me/${id}`, data),
  canceOvertimeRequest: (id: string) =>
    ApiClient.PATCH<IOvertime>(`/hrm/overtime-requests/me/${id}/cancel`),
  reviewOvertimeRequest: (id: string, isApprove: boolean) =>
    ApiClient.PATCH<IOvertime>(
      `/hrm/overtime-requests/${id}/${isApprove ? "approve" : "reject"}`,
    ),

  getAllOvertimeRequestByEmployee: (
    employeeId: string,
    query?: IOvertimeRequestQuery,
  ) =>
    ApiClient.GET<IPaginatedResponse<IOvertime>>(
      `/hrm/overtime-requests/employee/${employeeId}`,
      query,
    ),
  getAllOvertimeRequest: (query?: IOvertimeRequestQuery) =>
    ApiClient.GET<IPaginatedResponse<IOvertime>>(
      `/hrm/overtime-requests`,
      query,
    ),
  getOvertimeRequestByManager: (query?: IOvertimeRequestQuery) =>
    ApiClient.GET<IPaginatedResponse<IOvertime>>(
      `/hrm/overtime-requests/my-department`,
      query,
    ),
  getMyOvertimeRequest: (query?: IOvertimeRequestQuery) =>
    ApiClient.GET<IPaginatedResponse<IOvertime>>(
      `/hrm/overtime-requests/me`,
      query,
    ),
  createStaionRequest: (query?: IOvertimeRequestQuery) =>
    ApiClient.GET<IPaginatedResponse<IOvertime>>(
      `/hrm/overtime-requests/me`,
      query,
    ),
  getOneOvertimeRequest: (id: string) =>
    ApiClient.GET<IOvertime>(`/hrm/overtime-requests/${id}`),
  removeOvertimeRequest: (id: string) =>
    ApiClient.DELETE<IOvertime>(`/hrm/overtime-requests/${id}`),

  getAllStationeryProducts: async (params?: IQueryStationeryProduct) => {
    const data = await ApiClient.GET<IPaginatedResponse<IStationeryProduct>>(
      "/hrm/stationery-products",
      params,
    );
    return data;
  },

  getOneStationeryProduct: async (id: number) => {
    const data = await ApiClient.GET<IStationeryProduct>(
      `/hrm/stationery-products/${id}`,
    );
    return data;
  },

  createStationeryProduct: async (payload: ICreateStationeryProduct) => {
    const data = await ApiClient.POST<IStationeryProduct>(
      "/hrm/stationery-products",
      payload,
    );
    return data;
  },

  updateStationeryProduct: async (
    id: number,
    payload: IUpdateStationeryProduct,
  ) => {
    const data = await ApiClient.PUT<IStationeryProduct>(
      `/hrm/stationery-products/${id}`,
      payload,
    );
    return data;
  },

  removeStationeryProduct: async (id: number) => {
    const data = await ApiClient.DELETE<IStationeryProduct>(
      `/hrm/stationery-products/${id}`,
    );
    return data;
  },

  getAllStationeryPeriods: async (params?: IQueryStationeryPeriod) => {
    const res = await ApiClient.GET<IPaginatedResponse<IStationeryPeriod>>(
      "/hrm/stationery-periods",
      params,
    );
    return res;
  },

  getOneStationeryPeriod: async (id: number) => {
    const data = await ApiClient.GET<IStationeryPeriodDetail>(
      `/hrm/stationery-periods/${id}`,
    );
    return data;
  },

  createStationeryPeriod: async (payload: ICreateStationeryPeriod) => {
    const data = await ApiClient.POST<IStationeryPeriod>(
      "/hrm/stationery-periods",
      payload,
    );
    return data;
  },

  closeStationeryPeriod: async (id: number) => {
    const data = await ApiClient.PATCH<IStationeryPeriod>(
      `/hrm/stationery-periods/${id}/close`,
    );
    return data;
  },

  openStationeryPeriod: async (id: number) => {
    const data = await ApiClient.PATCH<IStationeryPeriod>(
      `/hrm/stationery-periods/${id}/open`,
    );
    return data;
  },

  reviewStationeryPeriod: async (id: number, isApprove: boolean) => {
    const data = await ApiClient.PATCH<IStationeryPeriod>(
      `/hrm/stationery-periods/${id}/${isApprove ? "approve" : "reject"}`,
    );
    return data;
  },
  updateStationeryPeriod: async (
    id: number,
    data: Partial<IStationeryPeriod>,
  ) => {
    const response = await ApiClient.PATCH<IStationeryPeriod>(
      `/hrm/stationery-periods/${id}`,
      data,
    );
    return response;
  },
  createStationeryRequest: async (payload: ICreateStationeryRequest) => {
    const data = await ApiClient.POST<IStationeryRequest>(
      "/hrm/stationery-requests",
      payload,
    );
    return data;
  },
  updateStationeryRequestNote: async (id: number, note: string) => {
    const res = await ApiClient.PATCH<IStationeryRequestItem[]>(
      `/hrm/stationery-requests/${id}/note`,
      { note },
    );
    return res;
  },
  getOneStationeryRequest: async (id: number) => {
    const data = await ApiClient.GET<IStationeryRequest>(
      `/hrm/stationery-requests/${id}`,
    );
    return data;
  },
  getMyStationeryRequest: async (id: number) => {
    const data = await ApiClient.GET<IStationeryRequest>(
      `/hrm/stationery-requests/period/${id}/me`,
    );
    return data;
  },
  reviewBulkStationeryRequest: async (
    requestId: number,
    payload: IReviewStationeryRequestItem[],
  ) => {
    const data = await ApiClient.PATCH<IStationeryRequest>(
      `/hrm/stationery-request-items/${requestId}/review/bulk`,
      payload,
    );
    return data;
  },

  createStationeryRequestItemBulk: async (
    requestId: string | number,
    payload: Partial<ICreateStationeryRequestItem>[],
  ) => {
    const res = await ApiClient.POST<IStationeryRequestItem[]>(
      `/hrm/stationery-request-items/${requestId}`,
      payload,
    );
    return res;
  },

  updateStationeryRequestItemBulk: async (
    payload: IUpdateStationeryRequestItem[],
  ) => {
    const res = await ApiClient.PATCH<IStationeryRequestItem[]>(
      `/hrm/stationery-request-items/update`,
      payload,
    );
    return res;
  },

  reviewStationeryRequestItemBulk: async (
    requestId: string | number,
    payload: IReviewStationeryRequestItem[],
  ) => {
    const ress = await ApiClient.PATCH<IStationeryRequestItem[]>(
      `/hrm/stationery-request-items/${requestId}/review/bulk`,
      payload,
    );
    return ress;
  },
  removeBulkStationeryRequestItem: async (ids: number[]) => {
    const res = await ApiClient.DELETE<IStationeryRequestItem[]>(
      `/hrm/stationery-request-items/bulk`,
      {
        data: { ids },
      },
    );
    return res;
  },
  importStationeryProduct: async (payload: FormData) => {
    const res = await ApiClient.POST<IStationeryProduct[]>(
      `/hrm/stationery-products/import`,
      payload,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return res;
  },

  createMeetingSchedule: async (data: ICreateMeetingSchedule) => {
    const res = await ApiClient.POST<IMeetingSchedule>(
      "/hrm/meeting-schedules",
      data,
    );
    return res;
  },

  getAllMeetingSchedule: async (date?: any) => {
    const res = await ApiClient.GET<IMeetingSchedule[]>(
      "/hrm/meeting-schedules",
      date ? { date } : undefined,
    );
    return res;
  },
  getMyMeetingSchedule: async (date?: string) => {
    const res = await ApiClient.GET<IMeetingSchedule[]>(
      "/hrm/meeting-schedules/me",
      date ? { date } : undefined,
    );
    return res;
  },

  getMeetingScheduleById: async (id: string) => {
    const res = await ApiClient.GET<IMeetingSchedule>(
      `/hrm/meeting-schedules/${id}`,
    );
    return res;
  },

  updateMeetingSchedule: async (
    id: number | string,
    data: IUpdateMeetingSchedule,
  ) => {
    const res = await ApiClient.PUT<IMeetingSchedule>(
      `/hrm/meeting-schedules/${id}`,
      data,
    );
    return res;
  },
  reviewMeetingSchedule: async (
    id: number | string,
    approvalStatus: Status,
  ) => {
    const res = await ApiClient.PATCH<IMeetingSchedule>(
      `/hrm/meeting-schedules/${id}`,
      {
        approvalStatus,
      },
    );
    return res;
  },

  removeMeetingSchedule: async (id: number | string) => {
    const res = await ApiClient.DELETE<IMeetingSchedule>(
      `/hrm/meeting-schedules/${id}`,
    );
    return res;
  },

  createMeetingRoom: async (data: ICreateMeetingRoom) => {
    const res = await ApiClient.POST<IMeetingRoom>("/hrm/meeting-room", data);
    return res;
  },

  getAllMeetingRoom: async (params?: any) => {
    const res = await ApiClient.GET<IPaginatedResponse<IMeetingRoom>>(
      "/hrm/meeting-room",
      params,
    );
    return res;
  },

  updateMeetingRoom: async (id: number | string, data: IUpdateMeetingRoom) => {
    const res = await ApiClient.PATCH<IMeetingRoom>(
      `/hrm/meeting-room/${id}`,
      data,
    );
    return res;
  },

  removeMeetingRoom: async (id: number | string) => {
    const res = await ApiClient.DELETE<IMeetingRoom>(`/hrm/meeting-room/${id}`);
    return res;
  },

  getCategories: async (
    params: IQuery,
  ): Promise<IPaginatedResponse<IStationeryCategory>> => {
    const data = await ApiClient.GET("/hrm/stationery-category", params);
    return data;
  },

  createCategory: async (payload: {
    name: string;
  }): Promise<IStationeryCategory> => {
    const data = await ApiClient.POST("/hrm/stationery-category", payload);
    return data;
  },

  updateCategory: async ({
    id,
    ...payload
  }: {
    id: number;
    name: string;
  }): Promise<IStationeryCategory> => {
    const data = await ApiClient.PATCH(
      `${"/hrm/stationery-category"}/${id}`,
      payload,
    );
    return data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    const { data } = await ApiClient.DELETE(
      `${"/hrm/stationery-category"}/${id}`,
    );
    return data;
  },
};
